import React, { useLayoutEffect, useState, useRef } from 'react'
import { flushSync } from 'react-dom'
import { X, LoaderCircle, type LucideIcon } from 'lucide-react'
import { Button } from '@sourcepilot/ui/framework/ui/atoms/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@sourcepilot/ui/framework/ui/atoms/tooltip'
import { useAtomValue } from 'jotai'
import { pageLoadingAtom } from '@sourcepilot/ui/framework/state/session-data';interface TabProps {
  id: string
  title: string
  isActive: boolean
  onClick: () => void
  onClose: (e: React.MouseEvent) => void
  provided: any
  snapshot: any
  icon: LucideIcon | React.ElementType
}

const TabTitle = React.memo(({ title, isOverflowing, isActive }: { title: string; isOverflowing: boolean, isActive: boolean }) => {
  return (
    <span 
      className={`whitespace-nowrap inline-block w-full overflow-hidden pl-1 ${isActive ? 'font-bold' : ''}`}
      style={{
        maskImage: isOverflowing 
          ? 'linear-gradient(to left, transparent, black 3ch)'
          : 'none',
        WebkitMaskImage: isOverflowing 
          ? 'linear-gradient(to left, transparent, black 3ch)'
          : 'none',
      }}
    >
      {title}
    </span>
  );
});

const TabTitleWithTooltip = React.memo(({ title, onClick, containerWidth, isOverflowing, isActive }: { title: string; onClick: () => void; containerWidth: number; isOverflowing: boolean, isActive:boolean }) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div 
          onClick={onClick}
          className="flex-grow overflow-hidden relative h-full flex items-center pl-[6px]"
          style={{ width: `${containerWidth}px` }}
        >
          <span className="whitespace-nowrap absolute inset-0 flex items-center">
            <TabTitle title={title} isOverflowing={isOverflowing} isActive={isActive}/>
          </span>
        </div>
      </TooltipTrigger>
      {isOverflowing && (
        <TooltipContent side="bottom" align="start">
          {title}
        </TooltipContent>
      )}
    </Tooltip>
  );
});

export function Tab({ id, title, isActive, onClick, onClose, provided, snapshot, icon: Icon  }: TabProps) {
  const [tabWidth, setTabWidth] = useState<number>(0);
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);
  const textRef = useRef<HTMLSpanElement>(null);
  const isPageLoading = useAtomValue(pageLoadingAtom);
  
  // Check if this tab is loading (it's active and page is in loading state)
  const isTabLoading = false; //isActive && isPageLoading;

  // Adjust tab width based on text length and min/max constraints
  // The label's width comes from a ResizeObserver, which reports it after the
  // browser's own layout. Reading offsetWidth here forced a synchronous layout
  // of the whole page: on a big project the tab paid for laying out the page's
  // content (501 ms at 10k requirements; VC-UI's profile, 2026-10-19).
  useLayoutEffect(() => {
    const updateTabWidth = (textWidth: number) => {
      const iconWidth = 28;
      const hPadding = 10;
      const minWidth = Math.max(textWidth + 2*hPadding + iconWidth, 70); // Added 10px for padding
      const newWidth = Math.min(minWidth, 200);
      setTabWidth(newWidth);
      setIsOverflowing(textWidth > newWidth - (hPadding + iconWidth));
    };

    if (typeof ResizeObserver === 'undefined' || !textRef.current) return;
    // the observer reports the first size itself, once the page is laid out
    // and before it is painted: committed at once (flushSync), so no frame
    // shows the tab at its starting width
    const resizeObserver = new ResizeObserver((entries) => {
      const e = entries[entries.length - 1];
      if (!e) return;
      // the border box, as offsetWidth measured it: the label has padding,
      // which contentRect leaves out. Rounded as offsetWidth was. Where the
      // border box is not reported, the layout is fresh here, so reading
      // offsetWidth forces none.
      const box = Array.isArray(e.borderBoxSize) ? e.borderBoxSize[0] : undefined;
      const width = box ? Math.round(box.inlineSize) : (e.target as HTMLElement).offsetWidth;
      flushSync(() => updateTabWidth(width));
    });
    resizeObserver.observe(textRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [title]);

  return (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      className={`flex items-center relative z-0 text-[13px] ${'bg-background'} ${
        snapshot.isDragging ? 'shadow-lg' : ''} group px-1`}
      style={{
        ...provided.draggableProps.style,
        width: `${tabWidth}px`,
        minWidth: `${tabWidth}px`,
        maxWidth: `${tabWidth}px`,
        height: isActive ? '33px' : '32px',
        marginTop: '1px',
        borderTopWidth: '1px',
        borderLeftWidth: '1px',
        borderRightWidth: '1px',
        borderBottomWidth: isActive ? '0px' : '1px',
        borderStyle: 'solid',
        borderColor: '#B0B8C4',
        boxShadow: isActive ? '0 0 0 1px rgba(176, 184, 196, 0.5)' : 'none',
        zIndex: isActive ? 10 : 0,
        background: isActive ? 'white' : '#f3f4f6',
        color: isActive ? 'black' : 'rgb(75,85,99)',
        borderTopLeftRadius: '5px',
        borderTopRightRadius: '5px',
      }}
    >
      <div className="relative w-4 h-10 flex items-center justify-center"> 
        {isTabLoading ? (
          <LoaderCircle className="w-[24px] h-[24px] absolute px-[6px] animate-spin" />
        ) : (
          <Icon className="w-[24px] h-[24px] absolute px-[6px]" />
        )}
      </div>

      <span ref={textRef} className="absolute invisible whitespace-nowrap pl-1 hover:text-gray-800">{title}</span>
      
      <TabTitleWithTooltip 
        title={title} 
        onClick={onClick} 
        containerWidth={tabWidth - 28} 
        isOverflowing={isOverflowing}
        isActive={isActive}
      />

      <div className="relative w-4 h-10 flex items-center justify-center"> 
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-5 pl-0 pr-0 rounded-none flex-shrink-0 absolute" 
          onClick={onClose}
          aria-label={`Close ${title} tab`}
        >
          <X className="h-[22px] w-[22px] pr-[2px] text-[#2f4858] opacity-0 group-hover:opacity-100 transition-opacity" />
        </Button>
      </div>
    </div>
  )
}

